/**
 * `useLatestTranslationJob(templateId)` — React Query wrapper around
 * `getLatestTranslationJobForTemplate`.
 *
 * Auto-polls every 5 seconds while the most-recent job's `Status` is
 * still in-flight (`queued` / `running`). Stops polling the moment the
 * status terminates (`completed` / `failed`) or no job exists. The
 * Translations Tab uses this to drive a "translating…" banner and to
 * decide when to refetch the UILM diff.
 *
 * The webhook receiver (server-side, outside this app) is what flips
 * the row from `running` → `completed` / `failed`. This hook just
 * observes the result.
 */

import { useQuery } from '@tanstack/react-query';
import { getLatestTranslationJobForTemplate } from '../services/uilm-translation-job.service';
import type { UilmTranslationJob } from '../types/uilm-translation-job.types';

const QUERY_KEY = ['uilm-translation-job-latest'];
const POLL_INTERVAL_MS = 5000;

const isTerminal = (status: UilmTranslationJob['status']): boolean =>
  status === 'completed' || status === 'failed';

export const useLatestTranslationJob = (templateId: string, enabled = true) => {
  return useQuery<UilmTranslationJob | null>({
    queryKey: [...QUERY_KEY, templateId],
    queryFn: () => getLatestTranslationJobForTemplate(templateId),
    enabled: Boolean(templateId) && enabled,
    // Poll while a job is in flight; stop the moment it terminates or no
    // job exists. React Query passes the latest data into this callback,
    // letting us turn polling off without external state.
    refetchInterval: (query) => {
      const latest = query.state.data;
      if (!latest) return false;
      return isTerminal(latest.status) ? false : POLL_INTERVAL_MS;
    },
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
};
